import React, { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  Folder,
  FolderOpen,
  FolderPlus,
  Files,
  ChevronRight,
  Library,
  Plus,
  X,
  Pencil,
  Trash2,
} from "lucide-react";
import {
  createDocumentGroup,
  updateDocumentGroup,
  deleteDocumentGroup,
} from "../../../api/hrm/documents/groups";
import {
  showConfirmDialog,
  showSuccessToast,
  showErrorToast,
} from "../../../components/ConfirmDialog";
import { hasPermission } from "../../../utils/permissionUtils";

/** Chuẩn hóa parent_id: UUID string hoặc null */
const normParent = (value) => {
  if (value === null || value === undefined || value === "" || value === "null") {
    return null;
  }
  return String(value);
};

const normId = (value) => (value ? String(value) : "");

/**
 * Cây 2 cấp: Nhóm tài liệu (folder, parent_id null) → Phân loại (category).
 * selectedId: "" = Tất cả; uuid = nhóm / phân loại
 */
const DocumentGroupSidebar = ({
  groups = [],
  selectedId = "",
  totalAll = 0,
  onSelect,
  onGroupsChanged,
  collapsed = false,
  onToggleCollapsed,
}) => {
  const canCreate = hasPermission("hrm.documents", "C");
  const canUpdate = hasPermission("hrm.documents", "U");
  const canDelete = hasPermission("hrm.documents", "D");

  const [expanded, setExpanded] = useState(() => new Set());
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [nameInput, setNameInput] = useState("");
  const [descInput, setDescInput] = useState("");

  const normalizedGroups = useMemo(
    () =>
      (groups || []).map((g) => ({
        ...g,
        id: normId(g.id || g.group_id),
        parent_id: normParent(g.parent_id),
        group_name: g.group_name || g.name || "Không tên",
      })),
    [groups]
  );

  const selectedName = useMemo(() => {
    if (!selectedId) return "Tất cả tài liệu";
    const found = normalizedGroups.find((g) => g.id === selectedId);
    if (!found) return "Tất cả tài liệu";
    if (found.parent_id) {
      const p = normalizedGroups.find((g) => g.id === found.parent_id);
      return p ? `${p.group_name} / ${found.group_name}` : found.group_name;
    }
    return found.group_name;
  }, [selectedId, normalizedGroups]);

  const tree = useMemo(() => {
    const byId = new Map();
    normalizedGroups.forEach((g) => {
      if (g.id) byId.set(g.id, { ...g, children: [] });
    });

    const roots = [];
    const orphans = [];

    byId.forEach((node) => {
      const pid = node.parent_id;
      if (pid && byId.has(pid)) {
        byId.get(pid).children.push(node);
      } else if (!pid) {
        roots.push(node);
      } else {
        // parent_id trỏ tới nhóm không còn trong list → hiện tạm như gốc (tránh mất dữ liệu)
        orphans.push(node);
      }
    });

    const sortNodes = (list) =>
      list.sort(
        (a, b) =>
          (a.sort_index ?? 0) - (b.sort_index ?? 0) ||
          String(a.group_name || "").localeCompare(String(b.group_name || ""), "vi")
      );

    roots.forEach((r) => sortNodes(r.children));
    sortNodes(roots);
    sortNodes(orphans);

    return [...roots, ...orphans];
  }, [normalizedGroups]);

  // Tự mở các nhóm đang có phân loại / đang được chọn (giữ phân cấp luôn thấy)
  useEffect(() => {
    setExpanded((prev) => {
      const next = new Set(prev);
      tree.forEach((folder) => {
        if (
          folder.children?.length > 0 ||
          selectedId === folder.id ||
          folder.children?.some((c) => c.id === selectedId)
        ) {
          next.add(folder.id);
        }
      });
      return next;
    });
  }, [tree, selectedId]);

  const openCreateFolder = () => {
    setForm({ mode: "create", parentId: null, node: null });
    setNameInput("");
    setDescInput("");
  };

  const openCreateCategory = (parentId) => {
    const pid = normId(parentId);
    if (!pid) {
      showErrorToast("Không xác định được Nhóm tài liệu cha.");
      return;
    }
    setForm({ mode: "create", parentId: pid, node: null });
    setNameInput("");
    setDescInput("");
    setExpanded((prev) => new Set(prev).add(pid));
  };

  const openEdit = (node) => {
    setForm({
      mode: "edit",
      parentId: normParent(node.parent_id),
      node,
    });
    setNameInput(node.group_name || "");
    setDescInput(node.description || "");
  };

  const closeForm = () => {
    if (saving) return;
    setForm(null);
    setNameInput("");
    setDescInput("");
  };

  const handleSave = async () => {
    const name = nameInput.trim();
    if (!name) {
      showErrorToast("Vui lòng nhập tên.");
      return;
    }
    setSaving(true);
    try {
      if (form.mode === "edit" && form.node?.id) {
        await updateDocumentGroup(form.node.id, {
          group_name: name,
          description: descInput.trim() || null,
          parent_id: normParent(form.node.parent_id),
        });
        showSuccessToast("Đã cập nhật.");
      } else {
        const parentId = normParent(form.parentId);
        await createDocumentGroup({
          group_name: name,
          description: descInput.trim() || null,
          parent_id: parentId,
        });
        if (parentId) {
          setExpanded((prev) => new Set(prev).add(parentId));
        }
        showSuccessToast(
          parentId ? "Đã tạo Phân loại." : "Đã tạo Nhóm tài liệu."
        );
      }
      closeForm();
      onGroupsChanged?.();
    } catch (err) {
      showErrorToast(
        err?.response?.data?.message || "Không thể lưu. Vui lòng thử lại."
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (node) => {
    const isFolder = !normParent(node.parent_id);
    showConfirmDialog({
      title: isFolder ? "Xóa Nhóm tài liệu?" : "Xóa Phân loại?",
      text: `«${node.group_name}» sẽ bị xóa vĩnh viễn. Chỉ xóa được khi không còn ${
        isFolder ? "phân loại con / tài liệu" : "tài liệu"
      }.`,
      confirmText: "Xóa",
    }).then(async (result) => {
      if (!result.isConfirmed) return;
      try {
        await deleteDocumentGroup(node.id);
        showSuccessToast("Đã xóa.");
        if (selectedId === node.id) onSelect?.("");
        onGroupsChanged?.();
      } catch (err) {
        showErrorToast(err?.response?.data?.message || "Không thể xóa.");
      }
    });
  };

  const toggleExpand = (id) => {
    const key = normId(id);
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const isFormCategory =
    form &&
    (Boolean(normParent(form.parentId)) ||
      (form.mode === "edit" && Boolean(normParent(form.node?.parent_id))));
  const formTitle =
    form?.mode === "edit"
      ? form.node?.parent_id
        ? "Đổi tên Phân loại"
        : "Đổi tên Nhóm tài liệu"
      : isFormCategory
        ? "Tạo Phân loại"
        : "Tạo Nhóm tài liệu";

  const parentFolderName = form?.parentId
    ? normalizedGroups.find((g) => g.id === form.parentId)?.group_name
    : null;

  const renderNodeActions = (node) => {
    const isFolder = !normParent(node.parent_id);
    if (!canUpdate && !canDelete && !(canCreate && isFolder)) return null;
    return (
      <div
        className="hidden lg:flex items-center gap-0.5 opacity-0 group-hover/node:opacity-100 transition-opacity"
        onClick={(ev) => ev.stopPropagation()}
      >
        {canCreate && isFolder ? (
          <button
            type="button"
            title="Thêm Phân loại (thư mục con)"
            onClick={() => openCreateCategory(node.id)}
            className="h-6 w-6 inline-flex items-center justify-center rounded-md text-red-600 hover:bg-red-100"
          >
            <Plus size={14} />
          </button>
        ) : null}
        {canUpdate ? (
          <button
            type="button"
            title="Đổi tên"
            onClick={() => openEdit(node)}
            className="h-6 w-6 inline-flex items-center justify-center rounded-md text-gray-500 hover:bg-gray-100"
          >
            <Pencil size={13} />
          </button>
        ) : null}
        {canDelete ? (
          <button
            type="button"
            title="Xóa"
            onClick={() => handleDelete(node)}
            className="h-6 w-6 inline-flex items-center justify-center rounded-md text-red-500 hover:bg-red-50"
          >
            <Trash2 size={13} />
          </button>
        ) : null}
      </div>
    );
  };

  return (
    <aside
      data-tour="hrm-documents-groups"
      className={`flex flex-col bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden transition-all ${
        collapsed ? "h-auto min-h-0" : "h-full min-h-[280px] lg:min-h-[520px]"
      }`}
    >
      <div className="shrink-0 px-4 py-3 sm:py-3.5 border-b border-gray-100 bg-gradient-to-r from-red-50/80 via-white to-white">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="h-9 w-9 rounded-xl bg-red-600 text-white flex items-center justify-center shadow-sm shadow-red-200 shrink-0">
              <Library size={18} />
            </div>
            <div className="min-w-0">
              <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-red-600/80">
                Thư viện
              </p>
              <div className="flex items-center gap-1.5 min-w-0">
                <h2 className="text-sm font-black uppercase tracking-tight text-gray-800 truncate">
                  Nhóm tài liệu
                </h2>
                {collapsed && (
                  <span className="lg:hidden text-[11px] font-semibold text-red-700 bg-red-50 border border-red-200 px-2 py-0.5 rounded-md truncate max-w-[130px]">
                    {selectedName}
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            {canCreate ? (
              <button
                type="button"
                onClick={openCreateFolder}
                title="Tạo Nhóm tài liệu"
                className="hidden lg:inline-flex h-8 w-8 items-center justify-center rounded-lg bg-red-600 text-white hover:bg-red-700 shadow-sm"
              >
                <FolderPlus size={16} />
              </button>
            ) : null}
            {onToggleCollapsed ? (
              <button
                type="button"
                onClick={onToggleCollapsed}
                className="lg:hidden inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-red-200 bg-red-50 text-red-700 text-xs font-bold active:scale-95 transition-all"
                aria-label={collapsed ? "Mở danh mục" : "Thu gọn"}
              >
                <span>{collapsed ? "Chọn nhóm" : "Thu gọn"}</span>
                <ChevronRight
                  size={14}
                  className={`transition-transform duration-200 ${collapsed ? "" : "rotate-90"}`}
                />
              </button>
            ) : null}
          </div>
        </div>
      </div>

      {!collapsed && (
        <nav className="flex-1 max-h-72 lg:max-h-none overflow-y-auto p-2 space-y-0.5 custom-scrollbar">
          <button
            type="button"
            onClick={() => onSelect?.("")}
            className={`w-full flex items-center gap-2.5 rounded-xl px-3 py-2.5 text-left transition-all ${
              selectedId === ""
                ? "bg-red-50 border border-red-200 text-red-800 shadow-sm"
                : "border border-transparent text-gray-700 hover:bg-gray-50 hover:border-gray-100"
            }`}
          >
            <Files
              size={18}
              className={`shrink-0 ${selectedId === "" ? "text-red-600" : "text-gray-400"}`}
            />
            <span className="flex-1 min-w-0 text-sm font-semibold truncate">
              Tất cả tài liệu
            </span>
            <span
              className={`shrink-0 tabular-nums text-[11px] font-bold px-2 py-0.5 rounded-full ${
                selectedId === ""
                  ? "bg-red-600 text-white"
                  : "bg-gray-100 text-gray-500"
              }`}
            >
              {totalAll}
            </span>
          </button>

          {tree.map((folder) => {
            const hasChildren = (folder.children?.length || 0) > 0;
            const isOpen =
              expanded.has(folder.id) ||
              selectedId === folder.id ||
              folder.children.some((c) => c.id === selectedId);
            const folderActive = selectedId === folder.id;
            const FolderIcon = folderActive || isOpen ? FolderOpen : Folder;
            const isOrphanCategory = Boolean(folder.parent_id);

            return (
              <div key={folder.id} className="space-y-0.5">
                <div
                  className={`group/node w-full flex items-center gap-1 rounded-xl px-1.5 py-1.5 transition-all ${
                    folderActive
                      ? "bg-red-50 border border-red-200 text-red-800 shadow-sm"
                      : "border border-transparent text-gray-700 hover:bg-gray-50 hover:border-gray-100"
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => toggleExpand(folder.id)}
                    className={`h-7 w-7 shrink-0 inline-flex items-center justify-center rounded-lg text-gray-400 hover:bg-white/80 ${
                      hasChildren || isOrphanCategory ? "" : "invisible"
                    }`}
                    aria-label={isOpen ? "Thu gọn" : "Mở rộng"}
                  >
                    <ChevronRight
                      size={14}
                      className={`transition-transform ${isOpen ? "rotate-90" : ""}`}
                    />
                  </button>
                  <button
                    type="button"
                    onClick={() => onSelect?.(folder.id)}
                    className="flex-1 min-w-0 flex items-center gap-2 py-1 text-left"
                  >
                    <FolderIcon
                      size={17}
                      className={`shrink-0 ${
                        folderActive ? "text-red-600" : "text-amber-500"
                      }`}
                      strokeWidth={folderActive ? 2.25 : 2}
                    />
                    <span className="flex-1 min-w-0 text-sm font-semibold truncate">
                      {folder.group_name}
                    </span>
                    <span
                      className={`shrink-0 tabular-nums text-[11px] font-bold px-2 py-0.5 rounded-full ${
                        folderActive
                          ? "bg-red-600 text-white"
                          : "bg-gray-100 text-gray-500"
                      }`}
                    >
                      {folder.total ?? 0}
                    </span>
                  </button>
                  {renderNodeActions(folder)}
                </div>

                {isOpen &&
                  folder.children.map((cat) => {
                    const catActive = selectedId === cat.id;
                    return (
                      <div
                        key={cat.id}
                        className={`group/node ml-6 flex items-center gap-1 rounded-xl border-l-2 border-red-100 pl-2 pr-2 py-1.5 transition-all ${
                          catActive
                            ? "bg-red-50 border border-red-200 border-l-red-400 text-red-800 shadow-sm"
                            : "border-transparent text-gray-600 hover:bg-gray-50 hover:border-gray-100"
                        }`}
                      >
                        <button
                          type="button"
                          onClick={() => onSelect?.(cat.id)}
                          className="flex-1 min-w-0 flex items-center gap-2 py-0.5 text-left"
                        >
                          <Folder
                            size={15}
                            className={`shrink-0 ${
                              catActive ? "text-red-600" : "text-rose-400"
                            }`}
                          />
                          <span className="flex-1 min-w-0 text-[13px] font-medium truncate">
                            {cat.group_name}
                          </span>
                          <span
                            className={`shrink-0 tabular-nums text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                              catActive
                                ? "bg-red-600 text-white"
                                : "bg-gray-100 text-gray-500"
                            }`}
                          >
                            {cat.total ?? 0}
                          </span>
                        </button>
                        {renderNodeActions(cat)}
                      </div>
                    );
                  })}
              </div>
            );
          })}

          {tree.length === 0 && (
            <p className="px-3 py-4 text-xs text-gray-400 italic text-center">
              Chưa có nhóm tài liệu.
              {canCreate ? " Nhấn + để tạo thư mục đầu tiên." : ""}
            </p>
          )}
        </nav>
      )}

      <AnimatePresence>
        {form ? (
          <motion.div
            className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/40"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeForm}
          >
            <motion.div
              role="dialog"
              aria-modal="true"
              initial={{ opacity: 0, scale: 0.96, y: 8 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 8 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-md rounded-2xl bg-white shadow-xl border border-gray-100 overflow-hidden"
            >
              <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 bg-gradient-to-r from-red-50 to-white">
                <div className="flex items-center gap-2">
                  <FolderPlus className="text-red-600" size={20} />
                  <h3 className="text-base font-bold text-gray-800">{formTitle}</h3>
                </div>
                <button
                  type="button"
                  onClick={closeForm}
                  className="h-8 w-8 rounded-full hover:bg-gray-100 inline-flex items-center justify-center"
                >
                  <X size={18} />
                </button>
              </div>
              <div className="p-5 space-y-4">
                {form.parentId ? (
                  <p className="text-xs text-gray-500 rounded-lg bg-red-50 border border-red-100 px-3 py-2">
                    Thư mục con của:{" "}
                    <span className="font-semibold text-red-700">
                      {parentFolderName || "—"}
                    </span>
                  </p>
                ) : null}
                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-1">
                    Tên <span className="text-red-500">*</span>
                  </label>
                  <input
                    autoFocus
                    value={nameInput}
                    onChange={(e) => setNameInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleSave();
                    }}
                    placeholder={
                      isFormCategory
                        ? "VD: Hợp đồng lao động"
                        : "VD: Nhân sự, Đào tạo…"
                    }
                    className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-red-200 focus:border-red-400"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-1">
                    Mô tả
                  </label>
                  <textarea
                    value={descInput}
                    onChange={(e) => setDescInput(e.target.value)}
                    rows={2}
                    placeholder="Tùy chọn"
                    className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-red-200 focus:border-red-400 resize-none"
                  />
                </div>
              </div>
              <div className="px-5 py-4 border-t border-gray-100 flex justify-end gap-2 bg-gray-50/50">
                <button
                  type="button"
                  disabled={saving}
                  onClick={closeForm}
                  className="h-10 px-4 rounded-xl border border-gray-200 bg-white text-sm font-medium text-gray-600 hover:bg-gray-50"
                >
                  Hủy
                </button>
                <button
                  type="button"
                  disabled={saving}
                  onClick={handleSave}
                  className="h-10 px-4 rounded-xl bg-red-600 text-white text-sm font-semibold hover:bg-red-700 disabled:opacity-60"
                >
                  {saving ? "Đang lưu…" : "Lưu"}
                </button>
              </div>
            </motion.div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </aside>
  );
};

export default DocumentGroupSidebar;
